import React, {createContext, useEffect, useState} from "react";
import {SxProps} from "@mui/joy/styles/types";
import {applyTransparency} from "../theme";

export type ThemeValueSelector = "light" | "dark" | "glass"

export type SideBarStyleProps = {
    selectedTextColor?: string,
    textColor?: string,
    backgroundColor?: string,
    hoverMenuBackgroundColor?: string,
    icon?: string,
    selectedMenuBgColor?: string,
    menuItemBgColor?: string,
    selectedMenuTextColor?: string,
    backdropFilter?: string,
    border?: string,
    hoverTextColor?: string,
}

export type NavigationStyleProps = SxProps & {
    selectedBgColor?: string,
    hoverBgColor?: string,
}


export type IThemeContextState = {
    theme?: ThemeValueSelector,
    setTheme?: (theme: ThemeValueSelector) => void,
    backgroundStyle?: Partial<SxProps>,
    sideBarProps?: SideBarStyleProps,
    topBarOverlayProps?: Partial<SxProps>,
    accentStyle?: Partial<SxProps>,
    navigationStyle?: NavigationStyleProps,
    fullLogo?: any,
    logo?: any
}


export const IThemeContext = createContext<IThemeContextState>({})

const ThemeContext = (props: { children }) => {
    const [theme, setTheme] = useState<"light" | "dark" | "glass">(localStorage.getItem("linguascore-theme") as any || "light")

    const fullLogo: { [key in ThemeValueSelector]: any } = {
        dark: require("../assets/full-logo.png"),
        light: require("../assets/full-logo.png"),
        glass: require("../assets/full-logo-gradient.png")
    }

    const logo: { [key in ThemeValueSelector]: any } = {
        dark: require("../assets/logo_regular.png"),
        light: require("../assets/logo_regular.png"),
        glass: require("../assets/logo.png")
    }

    const themeBgStyle: { [key in ThemeValueSelector]: Partial<SxProps> } = {
        dark: {
            backgroundColor: "background.surface"
        },
        light: {
            backgroundColor: "background.surface"
        },
        glass: {
            background: "linear-gradient(to bottom right, #0f172a, #6c0259, #0f172a)"
        }
    }
    const sideBarProps: { [key in ThemeValueSelector]: SideBarStyleProps } = {
        glass: {
            selectedTextColor: "white",
            textColor: "primary.200",
            hoverMenuBackgroundColor: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(24px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            hoverTextColor: "white",
        },
        light: {
            selectedTextColor: "primary.500",
            selectedMenuBgColor: applyTransparency("#ff69d9", 0.7),
            textColor: "text.primary",
            hoverMenuBackgroundColor: "rgba(0,0,0,0.1)",
            backgroundColor: "background.level1",
        },
        dark: {
            selectedTextColor: "primary.500",
            selectedMenuBgColor: applyTransparency("#ff69d9", 0.5),
            textColor: "text.primary",
            hoverMenuBackgroundColor: "rgba(255,255,255,0.2)",
            backgroundColor: "background.level1",
        },
    }

    const topBarOverlayProps: { [key in ThemeValueSelector]?: Partial<SxProps> } = {
        glass: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(0,0,0,0.3)"
        },
        dark: {
            backgroundColor: "background.level1"
        },
        light: {
            backgroundColor: "background.level1"
        },
    }

    const accentStyle: { [key in ThemeValueSelector]?: Partial<SxProps> } = {
        glass: {
            background: "linear-gradient(to right, #3b82f6, #ff2dd3)",
        },
        light: {
            backgroundColor: "secondary.500",
            color: "white"
        },
        dark: {
            backgroundColor: "secondary.600"
        },
    }

    const navigationStyle: { [key in ThemeValueSelector]?: NavigationStyleProps } = {
        dark: {
            selectedBgColor: "primary.500",
            backgroundColor: "neutral.700",
            hoverBgColor: "rgba(255,255,255,0.6)",
        },
        light: {
            selectedBgColor: "primary.500",
            backgroundColor: "neutral.200",
            hoverBgColor: "rgba(255,255,255,0.6)",
        },
        glass: {
            color: "white",
            background: "linear-gradient(to right, #c500a2, #ff7bdc)",
            hoverBgColor: "rgba(255,255,255,0.4)",
            backdropFilter: "blur(48px)",
        }

    }

    useEffect(() => {
        localStorage.setItem("joy-color-scheme-dark", theme === "glass" ? "dark" : theme)
        localStorage.setItem("joy-color-scheme", theme === "glass" ? "dark" : theme)
        localStorage.setItem("joy-mode", theme === "glass" ? "dark" : theme)
        localStorage.setItem("linguascore-theme", theme)
    }, [theme])

    return <IThemeContext.Provider
        value={{
            theme,
            setTheme,
            fullLogo:fullLogo[theme],
            backgroundStyle: themeBgStyle[theme],
            sideBarProps: sideBarProps[theme],
            topBarOverlayProps: topBarOverlayProps[theme],
            accentStyle: accentStyle[theme],
            navigationStyle: navigationStyle[theme],
            logo:logo[theme],
        }}>
        {props.children}
    </IThemeContext.Provider>
}

export default ThemeContext